// Mock data for Caregivers Alliance Homecare (CAC) - frontend only
import {
  HeartHandshake, Users, Home, ShieldCheck, Utensils, Car,
  Clock, Award, Phone, Sparkles, Stethoscope
} from "lucide-react";

export const company = {
  name: "Caregivers Alliance Homecare",
  shortName: "CAC",
  legal: "Caregivers Alliance Homecare",
  tagline: "Compassionate care, right at home.",
  mission:
    "We prioritize fair wages, career advancement, and community engagement for our CAC workforce.",
  phone: "(614) 526-1548",
  phoneRaw: "6145261548",
  fax: "(614) 526-1548",
  email: "info@myohcare.com",
  website: "myohcare.com",
  calendly: "https://calendly.com/myohcare-info/new-meeting",
  address: "6161 Busch Blvd, Suite 119",
  city: "Columbus, OH 43229",
  hours: "Mon–Thu 10:00am – 2:00pm • Fri by appointment",
};

// Global DODD certification configuration (spec: auto-switch wording after effective date)
export const DODD_EFFECTIVE_DATE = "2026-10-07";
export const isDoddActive = () => new Date() >= new Date(`${DODD_EFFECTIVE_DATE}T00:00:00`);
export const doddServices = [
  "Homemaker Personal Care (HPC)",
  "HPC Transportation",
  "Non-Medical Transportation (NMT)",
  "Community Respite",
  "Residential Respite",
  "Shared Living",
  "Participant Directed HPC",
];

export const navLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "DODD Services", path: "/dodd" },
  { label: "MyCare", path: "/mycare" },
  { label: "Private Pay", path: "/private-pay" },
  { label: "Family Caregiver", path: "/family-caregiver" },
  { label: "Refer a Client", path: "/refer" },
  { label: "For Professionals", path: "/providers" },
  { label: "Careers", path: "/careers" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export const navMenu = [
  { label: "Home", path: "/" },
  {
    label: "Services", path: "/services",
    children: [
      { label: "All Services", path: "/services" },
      { label: "DODD Services", path: "/dodd" },
      { label: "HPC (Homemaker Personal Care)", path: "/hpc" },
      { label: "Respite Care", path: "/respite-care" },
      { label: "Private Pay", path: "/private-pay" },
      { label: "Next Generation MyCare", path: "/mycare" },
      { label: "Family Caregiver Options", path: "/family-caregiver" },
      { label: "How to Get Home Care in Ohio", path: "/how-to-get-home-care-in-ohio" },
    ],
  },
  {
    label: "For Professionals", path: "/providers",
    children: [
      { label: "County Boards & SSAs", path: "/providers" },
      { label: "Referral Partners", path: "/referral-partners" },
      { label: "Provider Info Sheet", path: "/provider-sheet" },
    ],
  },
  { label: "Careers", path: "/careers" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export const heroImage =
  "https://images.unsplash.com/photo-1765896387387-0538bc9f997e";

export const stats = [
  { value: "7", label: "DODD-certified services" },
  { value: "88", label: "Ohio counties — referral coverage" },
  { value: "5", label: "Core home care services" },
  { value: "Columbus", label: "Locally owned & operated" },
];

export const services = [
  {
    id: "personal-care",
    icon: HeartHandshake,
    title: "Personal Care",
    short: "Respectful help with bathing, grooming, dressing, mobility and daily living.",
    image: "https://images.unsplash.com/photo-1580869318757-a6c605b061ed",
    details: [
      "Bathing, grooming & hygiene assistance",
      "Dressing and mobility support",
      "Toileting and incontinence care",
      "Transfers and fall-risk monitoring",
    ],
  },
  {
    id: "homemaker-services",
    icon: Utensils,
    title: "Homemaker Services",
    short: "Keeping the home clean, safe and comfortable with everyday household help.",
    image: "https://images.pexels.com/photos/7345443/pexels-photo-7345443.jpeg",
    details: [
      "Meal planning & preparation",
      "Light housekeeping & laundry",
      "Grocery shopping & errands",
      "Medication reminders",
    ],
  },
  {
    id: "companionship",
    icon: Users,
    title: "Companionship",
    short: "Friendly company, conversation and meaningful engagement at home.",
    image: "https://images.unsplash.com/photo-1762955911431-4c44c7c3f408",
    details: [
      "Companionship & conversation",
      "Games, hobbies & activities",
      "Accompaniment to appointments",
      "Emotional support & check-ins",
    ],
  },
  {
    id: "respite-care",
    icon: Home,
    title: "Respite Care",
    short: "Short-term relief so family caregivers can rest and recharge.",
    image: "https://images.unsplash.com/photo-1543333995-a78aea2eee50",
    details: [
      "Hourly or overnight relief care",
      "Trusted, vetted caregivers",
      "Flexible scheduling",
      "Peace of mind for families",
    ],
  },
  {
    id: "transportation-support",
    icon: Car,
    title: "Transportation Support",
    short: "Safe, reliable rides to appointments, errands and social outings.",
    image: "https://images.pexels.com/photos/6647024/pexels-photo-6647024.jpeg",
    details: [
      "Rides to medical appointments",
      "Errands, shopping & pharmacy trips",
      "Social outings & community events",
      "Door-to-door assistance",
    ],
  },
];

export const programs = [
  {
    name: "MyCare Ohio",
    desc: "Certified provider for the MyCare Ohio managed care program.",
  },
  {
    name: "PASSPORT",
    desc: "Approved under Ohio's PASSPORT Medicaid waiver for home care.",
  },
  {
    name: "DODD",
    desc: "Certified by the Ohio Dept. of Developmental Disabilities.",
  },
];

// Managed-care plans & special programs we work with (frontend mock)
export const insurancePlans = [
  { name: "MyCare Ohio", full: "MyCare Ohio Managed Care", color: "#0E5A72", logo: "/logos/medicaid.png" },
  { name: "Molina", full: "Molina Healthcare", color: "#0072CE", logo: "/logos/molina.svg" },
  { name: "CareSource", full: "CareSource", color: "#00A651", logo: "/logos/caresource.svg" },
  { name: "Buckeye", full: "Buckeye Health Plan", color: "#C8102E", logo: "/logos/buckeye.png" },
  { name: "Anthem", full: "Anthem Blue Cross Blue Shield", color: "#1F4E96", logo: "/logos/anthem.svg" },
  { name: "DODD", full: "Ohio Dept. of Developmental Disabilities", color: "#1F4E96", logo: "/logos/dodd.png" },
  { name: "PASSPORT", full: "Ohio PASSPORT Waiver", color: "#23875A", logo: "/logos/aging.png" },
  { name: "Private Pay", full: "Private Pay Welcome", color: "#0E5A72", logo: "" },
];

// Veterans-focused care (private homes / VA / private pay)
export const veteransCare = {
  eyebrow: "Serving Those Who Served",
  title: "Dedicated home care for our veterans",
  subtitle:
    "We proudly support veterans in private homes and veterans' residences across Ohio — helping them live independently and with dignity through personalized help with daily activities.",
  image: "https://images.unsplash.com/photo-1543333995-a78aea2eee50",
  points: [
    "Assistance with daily activities — bathing, dressing, grooming & mobility",
    "Meal preparation, medication reminders & light housekeeping",
    "Compassionate companionship and dignity-first, respectful care",
    "Care provided in private homes and veterans' residences",
    "VA programs and private pay welcome",
  ],
};

// Intro video section
export const videoSection = {
  eyebrow: "Watch",
  title: "See how we bring care home",
  subtitle:
    "A quick look at the compassionate, personalized home care Caregivers Alliance provides across Ohio.",
  videoUrl: "https://customer-assets-cm19k8pv.emergentagent.net/job_owner-dashboard-care/artifacts/z2ohohej_transcpit_Caregivers_Alliance.mp4",
  poster: "https://images.unsplash.com/photo-1559234938-b60fff04894d",
};

export const whyChooseUs = [
  { icon: ShieldCheck, title: "Licensed & Certified", desc: "MyCare Ohio, PASSPORT and DODD certified for trusted, compliant care." },
  { icon: Clock, title: "Responsive Scheduling", desc: "Fast intake response and flexible scheduling built around each client." },
  { icon: Award, title: "Vetted Caregivers", desc: "Background-checked, trained and continually developed professionals." },
  { icon: HeartHandshake, title: "Person-Centered", desc: "Individualized care plans built around each client's needs and goals." },
  { icon: Sparkles, title: "Fair-Wage Workforce", desc: "We invest in our caregivers so they invest in your loved ones." },
  { icon: Stethoscope, title: "Care Coordination", desc: "We work closely with hospitals, case managers and physicians." },
];

export const serviceAreas = [
  "Columbus", "Dublin", "Westerville", "Gahanna", "Reynoldsburg",
  "Hilliard", "Grove City", "Worthington", "Upper Arlington",
  "New Albany", "Pickerington", "Delaware",
];

// All 88 Ohio counties (DODD operates through county boards in every county)
export const ohioCounties = [
  "Adams", "Allen", "Ashland", "Ashtabula", "Athens", "Auglaize", "Belmont",
  "Brown", "Butler", "Carroll", "Champaign", "Clark", "Clermont", "Clinton",
  "Columbiana", "Coshocton", "Crawford", "Cuyahoga", "Darke", "Defiance",
  "Delaware", "Erie", "Fairfield", "Fayette", "Franklin", "Fulton", "Gallia",
  "Geauga", "Greene", "Guernsey", "Hamilton", "Hancock", "Hardin", "Harrison",
  "Henry", "Highland", "Hocking", "Holmes", "Huron", "Jackson", "Jefferson",
  "Knox", "Lake", "Lawrence", "Licking", "Logan", "Lorain", "Lucas", "Madison",
  "Mahoning", "Marion", "Medina", "Meigs", "Mercer", "Miami", "Monroe",
  "Montgomery", "Morgan", "Morrow", "Muskingum", "Noble", "Ottawa", "Paulding",
  "Perry", "Pickaway", "Pike", "Portage", "Preble", "Putnam", "Richland",
  "Ross", "Sandusky", "Scioto", "Seneca", "Shelby", "Stark", "Summit",
  "Trumbull", "Tuscarawas", "Union", "Van Wert", "Vinton", "Warren",
  "Washington", "Wayne", "Williams", "Wood", "Wyandot",
];

// Fixed, professional signature appended to all outreach emails
export const emailSignature =
  "--\n" +
  "Caregivers Alliance Homecare\n" +
  "Home care headquartered in Columbus, Ohio — statewide DODD referral coverage\n" +
  "(614) 526-1548  |  info@myohcare.com  |  myohcare.com\n" +
  "Schedule a free consultation: https://calendly.com/myohcare-info/new-meeting\n" +
  "6161 Busch Blvd, Suite 119, Columbus, OH 43229\n" +
  "DODD Certified Provider  •  Contact us to verify plan eligibility, authorization & participation";


export const testimonials = [
  {
    name: "Maria T.",
    role: "Daughter of client",
    text: "The caregivers treat my mother like their own family. Reliable, kind and truly professional. I finally have peace of mind.",
    rating: 5,
  },
  {
    name: "James R.",
    role: "Hospital case manager",
    text: "Fast intake and excellent communication. CAC has become our go-to home care partner for safe hospital discharges.",
    rating: 5,
  },
  {
    name: "Denise W.",
    role: "Client",
    text: "My aide helps me stay independent at home. She's punctual, patient and genuinely cares. I couldn't ask for better.",
    rating: 5,
  },
];

export const careerPerks = [
  "Competitive, fair wages",
  "Paid training & certification support",
  "Career advancement pathways",
  "Flexible scheduling",
  "Supportive team culture",
  "Community engagement programs",
];

export const openRoles = [
  { title: "Home Health Aide (STNA)", type: "Full-time / Part-time", location: "Columbus, OH" },
  { title: "Personal Care Aide", type: "Full-time / Part-time", location: "Columbus, OH" },
  { title: "Companion Caregiver", type: "Part-time", location: "Greater Columbus" },
  { title: "Care Coordinator", type: "Full-time", location: "Columbus, OH (Hybrid)" },
];

export const leadSources = ["Hospital", "Nursing Home", "Family", "Website"];
export const leadStatuses = [
  "New", "Contacted", "Consultation Scheduled", "Intake Started",
  "Authorization Review", "Staffing Review", "Ready to Start", "Won", "Lost", "Nurture",
];

// Seed leads used by the owner dashboard (frontend mock)
export const seedLeads = [
  { id: "l1", name: "Robert Klein", contact: "(614) 555-0142", source: "Hospital", org: "OhioHealth Riverside", status: "New", note: "Discharge Fri, needs personal care 4x/week.", createdAt: "2025-07-14" },
  { id: "l2", name: "Ella Monroe", contact: "ella.m@email.com", source: "Family", org: "—", status: "Contacted", note: "Companion care for mother, weekdays.", createdAt: "2025-07-13" },
  { id: "l3", name: "Grant Whitfield", contact: "(614) 555-0199", source: "Nursing Home", org: "Wexner Heritage Village", status: "Consultation Scheduled", note: "Transitioning home, needs personal care support.", createdAt: "2025-07-12" },
  { id: "l4", name: "Sofia Alvarez", contact: "sofia@email.com", source: "Website", org: "—", status: "New", note: "Respite care, 2 weekends/month.", createdAt: "2025-07-15" },
  { id: "l5", name: "Harold Beck", contact: "(380) 555-0110", source: "Hospital", org: "Mount Carmel East", status: "Won", note: "Active client - homemaker support & reminders.", createdAt: "2025-07-02" },
  { id: "l6", name: "Priya Nair", contact: "(614) 555-0177", source: "Nursing Home", org: "Ohio Living Westminster", status: "Contacted", note: "Needs personal care support after a fall.", createdAt: "2025-07-11" },
];

// Marketing tool content (frontend). Edit freely to match your voice.
export const marketingContent = {
  goal: 10,
  outreach: [
    {
      id: "hospital",
      audience: "Hospital Discharge Planner",
      subject: "Reliable home care for your Central Ohio discharges",
      body:
        "Hi [Name],\n\nI'm reaching out from Caregivers Alliance Homecare here in Columbus. We provide personal care, companion care, respite care and medication reminders for patients transitioning home. Contact us to verify plan eligibility, authorization and current participation for DODD, Next Generation MyCare, PASSPORT and private-pay arrangements.\n\nWe offer fast intake to support safe, on-time discharges. Could we set up a quick 10-minute call to see how we can help your patients?\n\nThank you,",
    },
    {
      id: "nursing-home",
      audience: "Nursing Home / SNF Case Manager",
      subject: "Home care partner for residents returning home",
      body:
        "Hello [Name],\n\nCaregivers Alliance Homecare partners with skilled nursing facilities to support residents returning home. Our vetted caregivers help with daily living, mobility, companionship and medication reminders, and we coordinate closely with your care team.\n\nWe'd love to be a resource for your discharge planning. When is a good time to connect this week?\n\nWarm regards,",
    },
    {
      id: "physician",
      audience: "Physician / Clinic Referral",
      subject: "In-home support for your patients across Central Ohio",
      body:
        "Dear Dr. [Name],\n\nMany of your patients could stay safer and more independent at home with a little support. Caregivers Alliance Homecare provides compassionate in-home care across Ohio from our Columbus headquarters. We help families verify program eligibility and participation before services begin.\n\nI'd be glad to drop off referral pads and answer any questions. May I stop by the office briefly?\n\nSincerely,",
    },
  ],
  flyer:
    "COMPASSIONATE HOME CARE — COLUMBUS, OHIO\nCaregivers Alliance Homecare\n\n• Personal Care\n• Homemaker Personal Care\n• Companion Support\n• Respite Care\n• Transportation Support\n\nDODD Certified Provider\nDODD • Next Generation MyCare • PASSPORT • Private Pay — contact us to verify eligibility & participation\nBackground-checked caregivers\n\nCall today for a FREE consultation: (614) 526-1548\ninfo@myohcare.com | myohcare.com | Headquartered in Columbus • Statewide DODD referral coverage",
  social: [
    "Your loved one deserves to age comfortably at home. 💙 Caregivers Alliance Homecare provides trusted personal care, companion support & respite care across Ohio. Contact us to verify plan eligibility & participation. Free consultation: (614) 526-1548 #ColumbusOhio #HomeCare",
    "Caring for an aging parent is a full-time job. Let us help. Our vetted caregivers support daily living, mobility & medication reminders right at home. Contact us to verify DODD, MyCare, PASSPORT or private-pay options: (614) 526-1548 #CentralOhio #SeniorCare",
    "Coming home from the hospital? We make transitions safe and stress-free with responsive home care intake across Ohio. Call Caregivers Alliance Homecare: (614) 526-1548 #HomeCare #ColumbusOH",
  ],
};
